import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import {
  createExternalReview,
  CreateExternalReviewRequest,
  deleteExternalReview,
  ExternalReviewType,
  fetchExternalReviews,
  FetchExternalReviewsRequest,
  updateExternalReview,
  UpdateExternalReviewRequest,
} from './api/external-reviews-api';
import { useAlertStore } from './use-alert-store';

interface Store {
  isLoadingFetch: boolean;
  isLoadingAction: boolean;
  externalReviews: ExternalReviewType[];
  fetchExternalReviews: (payload: FetchExternalReviewsRequest) => Promise<void>;
  createExternalReview: (payload: CreateExternalReviewRequest) => Promise<void>;
  updateExternalReview: (payload: UpdateExternalReviewRequest) => Promise<void>;
  deleteExternalReview: (id: number) => Promise<void>;
  handleUpdateExternalReview: (payload: ExternalReviewType) => void;
  pagination?: PaginationType;
}

export const useExternalReviewsStore = create<Store>((set, get) => ({
  isLoadingFetch: false,
  isLoadingAction: false,
  externalReviews: [], // Данные загрузятся из API
  handleUpdateExternalReview: (payload) => {
    set((state) => {
      const isExist = state.externalReviews.some((item) => item.id === payload.id);

      if (!isExist) {
        return { externalReviews: [payload, ...state.externalReviews] };
      }
      const externalReviews = state.externalReviews.map((item) =>
        item.id === payload.id ? payload : item,
      );
      return { externalReviews };
    });
  },
  fetchExternalReviews: async (params) => {
    set({ isLoadingFetch: true });

    try {
      const { external_reviews, ...pagination } = await fetchExternalReviews(params);

      const isInfinite = !!params.infinite;

      const updatedReviews = isInfinite
        ? get().externalReviews.concat(external_reviews)
        : external_reviews;
      set({ externalReviews: updatedReviews, pagination });
    } catch (e) {
      console.error('Ошибка при загрузке источников отзывов:', e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по внешним отзывам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  createExternalReview: async (payload) => {
    set({ isLoadingAction: true });
    try {
      await createExternalReview(payload);
      await get().fetchExternalReviews({});
      useAlertStore.getState().add({ label: 'Внешний отзыв успешно создан', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({
        label: 'Не удалось создать внешний отзыв',
        variant: 'danger',
      });
      throw e;
    } finally {
      set({ isLoadingAction: false });
    }
  },
  updateExternalReview: async (payload) => {
    set({ isLoadingAction: true });
    try {
      await updateExternalReview(payload);
      await get().fetchExternalReviews({});
      useAlertStore.getState().add({
        label: 'Внешний отзыв успешно обновлен',
        variant: 'success',
      });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({
        label: 'Не удалось обновить внешний отзыв',
        variant: 'danger',
      });
      throw e;
    } finally {
      set({ isLoadingAction: false });
    }
  },
  deleteExternalReview: async (id) => {
    set({ isLoadingAction: true });
    try {
      const review = get().externalReviews.find((item) => item.id === id);

      // В API для удаления нужно отправлять aggregator_config_id,
      // который приходит из /aggregation/configs как aggregator_config_id
      const aggregatorConfigId = review?.aggregator_config_id ?? id;

      await deleteExternalReview(aggregatorConfigId);
      await get().fetchExternalReviews({});
      useAlertStore.getState().add({
        label: 'Внешний отзыв успешно удален',
        variant: 'success',
      });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({
        label: 'Не удалось удалить внешний отзыв',
        variant: 'danger',
      });
      throw e;
    } finally {
      set({ isLoadingAction: false });
    }
  },
}));
